import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  BellOff,
  Loader2,
  Trash2,
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Activity,
} from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import api from "../lib/api";
import { toast } from "sonner";
import { Zap } from "lucide-react";

const CONDITION_LABELS = {
  price_above: { label: "Price ≥", icon: TrendingUp, suffix: "$" },
  price_below: { label: "Price ≤", icon: TrendingDown, suffix: "$" },
  pct_change_above: { label: "24h ≥", icon: TrendingUp, suffix: "%" },
  pct_change_below: { label: "24h ≤", icon: TrendingDown, suffix: "%" },
  breakout_1m: { label: "1m breakout ≥", icon: Zap, suffix: "%" },
  sentiment_above: { label: "Sentiment ≥", icon: Activity, suffix: "" },
  sentiment_below: { label: "Sentiment ≤", icon: Activity, suffix: "" },
  rsi_overbought: { label: "RSI ≥", icon: Activity, suffix: "" },
  rsi_oversold: { label: "RSI ≤", icon: Activity, suffix: "" },
};

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/alerts");
      setAlerts(res.data.alerts || []);
    } catch (e) {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (alert) => {
    try {
      const res = await api.patch(`/alerts/${alert.id}`, { is_active: !alert.is_active });
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? res.data : a)));
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      toast.success("Alert removed");
    } catch (_) {
      toast.error("Failed to delete");
    }
  };

  const active = alerts.filter((a) => a.is_active);
  const paused = alerts.filter((a) => !a.is_active);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-8 py-8 md:py-12" data-testid="alerts-page">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B7068] hover:text-[#2A2F2B] mb-6 transition-soft"
          data-testid="alerts-back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Dashboard
        </Link>

        <header className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
              Price alerts
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B]">
            Your alerts
          </h1>
          <p className="text-[#6B7068] mt-2 text-sm leading-relaxed max-w-xl">
            We poll every minute and notify you instantly when a threshold is hit.
            One-hour cooldown per alert keeps things calm.
          </p>
        </header>

        {loading && (
          <div className="flex items-center gap-2 text-[#6B7068]">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading alerts...
          </div>
        )}

        {!loading && alerts.length === 0 && (
          <div className="rounded-2xl bg-white border border-[#EAE6E1] p-10 text-center" data-testid="alerts-empty">
            <Bell className="w-10 h-10 mx-auto mb-3 text-[#D18E63]" strokeWidth={1.25} />
            <h2 className="font-heading text-xl text-[#2A2F2B] mb-1">No alerts yet</h2>
            <p className="text-sm text-[#6B7068] max-w-md mx-auto mb-5">
              Open any asset page and tap "Set alert" to get notified when prices,
              percent changes, or RSI cross your target.
            </p>
            <Button asChild className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white">
              <Link to="/dashboard">Browse assets</Link>
            </Button>
          </div>
        )}

        {!loading && alerts.length > 0 && (
          <div className="space-y-8">
            <Section title={`Active (${active.length})`} alerts={active} onToggle={toggleActive} onRemove={remove} />
            {paused.length > 0 && (
              <Section title={`Paused (${paused.length})`} alerts={paused} onToggle={toggleActive} onRemove={remove} muted />
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function Section({ title, alerts, onToggle, onRemove, muted = false }) {
  return (
    <section data-testid={`alerts-section-${title.split(" ")[0].toLowerCase()}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068] mb-3">
        {title}
      </div>
      <div className="space-y-2">
        {alerts.map((a) => {
          const meta = CONDITION_LABELS[a.condition_type] || { label: a.condition_type, suffix: "" };
          const Icon = meta.icon || Bell;
          return (
            <div
              key={a.id}
              data-testid={`alert-row-${a.id}`}
              className={`rounded-2xl bg-white border border-[#EAE6E1] p-4 flex items-center gap-4 ${
                muted ? "opacity-60" : ""
              }`}
            >
              <div className="w-10 h-10 rounded-xl bg-[#FFF8F1] flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#D18E63]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/asset/${a.asset_type}/${a.symbol}`}
                  className="text-sm font-mono-ticker font-semibold text-[#2A2F2B] hover:text-[#547A8F]"
                >
                  {a.symbol}
                </Link>
                <span className="text-xs text-[#6B7068] ml-2">{a.name}</span>
                <div className="text-sm text-[#2A2F2B] mt-0.5">
                  {meta.label}{" "}
                  <span className="font-mono-ticker font-semibold">
                    {meta.suffix === "$" ? "$" : ""}
                    {a.threshold}
                    {meta.suffix === "%" ? "%" : ""}
                  </span>
                  {a.last_triggered_at && (
                    <span className="text-xs text-[#6B7068] ml-2">
                      · last fired {new Date(a.last_triggered_at).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={a.is_active}
                  onCheckedChange={() => onToggle(a)}
                  data-testid={`alert-toggle-${a.id}`}
                />
                <button
                  onClick={() => onRemove(a.id)}
                  data-testid={`alert-delete-${a.id}`}
                  className="p-2 rounded-full hover:bg-[#EAE6E1] text-[#6B7068]"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
